import { Box } from '@mui/material'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { toast } from 'react-hot-toast'
import { useState } from 'react'
import {
  RoleOption,
  TenantUser,
  useCurrentUser,
  useSetUserRole,
  useTenantUsers,
} from './Scheduler.data'
import { FONT_SANS, SchChip, SchDisplay, TOKENS } from './SchedulerDesign'
import { SchedulerPageFrame } from './SchedulerBrandBar'

/**
 * The tenant's scheduler users and the scheduler role each holds — client deck
 * 2026-09-16, slides 1-3.
 *
 * Only users whose planner `Planner_Roles` contains `scheduler` are listed:
 * without it they have no scheduler access at all. The Role column is the
 * scheduler's own `Scheduler_Roles` column, which is the only thing the
 * drop-down writes — never `Planner_Roles`, which the planner application
 * owns. A blank cell resolves to the default role (Super User) and is marked
 * so an admin can tell an unset user from a chosen one.
 *
 * Reachable with `view_users` (Super User) or `manage_users` (Admin). The
 * server decides what the caller may change: each row says whether it is
 * `editable`, and `assignableRoles` is what they may set (a Super User: User
 * and Viewer only, on someone who is one of those). The PUT enforces the same
 * rule, so typing a URL or a request gets a 403 that says why.
 */
export const ManageUsersPage = () => {
  const { data, isLoading, isError } = useTenantUsers()
  const { data: me } = useCurrentUser()
  const users = data?.users ?? []
  const options = data?.assignableRoles ?? []
  // Every role's name, so a row the caller cannot change still shows its role
  // (a Super User's list has Admins in it that their options do not name).
  const allRoles = data?.allRoles ?? options
  const defaultLabel =
    allRoles.find((o) => o.value === data?.defaultRole)?.label ?? 'Super User'
  const limited = data !== undefined && options.length < allRoles.length
  const [saving, setSaving] = useState<string | null>(null)

  const { mutate: setRole } = useSetUserRole(
    () => {
      setSaving(null)
      toast.success('Role updated')
    },
    (message) => {
      setSaving(null)
      toast.error(message)
    }
  )

  const onChange = (user: TenantUser, role: string) => {
    if (role === user.role && user.roleSource === 'assigned') return
    setSaving(user.email)
    setRole({ email: user.email, role })
  }

  return (
    <SchedulerPageFrame
      crumbs={[{ label: 'Manage users' }]}
      bodySx={{ px: 3, py: 2.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25 }}>
        <GroupOutlinedIcon sx={{ fontSize: 18, color: TOKENS.brandTeal }} />
        <SchDisplay>Manage users</SchDisplay>
      </Box>
      <Box sx={{ fontSize: 12, color: TOKENS.ink400, mb: 2 }}>
        Everyone with the scheduler role on this tenant
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: '10px 12px',
          mb: 2,
          border: `1px solid ${TOKENS.line200}`,
          borderRadius: '2px',
          background: TOKENS.blue50,
          fontFamily: FONT_SANS,
          fontSize: 12,
          color: TOKENS.ink700,
          lineHeight: 1.45,
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 15, color: TOKENS.blue600, mt: '1px' }}
        />
        <Box>
          These roles apply to the scheduler only and do not change anything in
          the planner. Someone missing from this list needs the scheduler role
          added in the planner&apos;s user administration first. A user with no
          role chosen yet is treated as {defaultLabel}.
          {limited && (
            <>
              {' '}
              As a Super User you can move people between User and Viewer; ask
              an Admin for any other change.
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          border: `1px solid ${TOKENS.line200}`,
          borderRadius: '2px',
          background: TOKENS.surface0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 300px',
            gap: '12px',
            px: '14px',
            py: '9px',
            background: TOKENS.panelHead,
            borderBottom: `1px solid ${TOKENS.line200}`,
            fontFamily: FONT_SANS,
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.ink400,
          }}
        >
          <Box>Email address</Box>
          <Box>Role</Box>
        </Box>

        {isLoading && <Msg>Loading…</Msg>}
        {isError && !isLoading && (
          <Msg>Could not load users for this tenant.</Msg>
        )}
        {!isLoading && !isError && users.length === 0 && (
          <Msg>No users have the scheduler role on this tenant.</Msg>
        )}

        {users.map((u) => {
          // Older service: no per-row answer, so fall back to the old rule.
          const editable = u.editable ?? data?.canAssign ?? false
          return (
            <Box
              key={u.email}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 300px',
                gap: '12px',
                alignItems: 'center',
                px: '14px',
                py: '8px',
                borderBottom: `1px solid ${TOKENS.line100}`,
                fontFamily: FONT_SANS,
                fontSize: 12,
                color: TOKENS.ink900,
              }}
            >
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.email}
                {me?.username && me.username.toLowerCase() === u.email && (
                  <Box component="span" sx={{ color: TOKENS.ink400, ml: 1 }}>
                    (you)
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RoleSelect
                  value={u.role}
                  options={editable ? options : allRoles}
                  disabled={!editable || saving === u.email}
                  title={
                    editable ? undefined : 'Only an Admin can change this role'
                  }
                  onChange={(role) => onChange(u, role)}
                />
                {saving === u.email ? (
                  <Box sx={{ fontSize: 11, color: TOKENS.ink400 }}>Saving…</Box>
                ) : u.roleSource === 'default' ? (
                  <SchChip tone="amber">Default</SchChip>
                ) : u.roleSource === 'invalid' ? (
                  <Box title={`Stored value "${u.storedRole}" is not a role`}>
                    <SchChip tone="failed">Invalid</SchChip>
                  </Box>
                ) : null}
              </Box>
            </Box>
          )
        })}
      </Box>
    </SchedulerPageFrame>
  )
}

const RoleSelect = ({
  value,
  options,
  disabled,
  title,
  onChange,
}: {
  value: string
  options: RoleOption[]
  disabled: boolean
  title?: string
  onChange: (v: string) => void
}) => (
  <Box
    component="select"
    value={value}
    disabled={disabled}
    title={title}
    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
      onChange(e.target.value)
    }
    sx={{
      width: 160,
      height: 28,
      px: '6px',
      borderRadius: '2px',
      border: `1px solid ${TOKENS.line300}`,
      background: disabled ? TOKENS.surface50 : TOKENS.surface0,
      fontFamily: FONT_SANS,
      fontSize: 12,
      color: TOKENS.ink900,
      cursor: disabled ? 'default' : 'pointer',
    }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </Box>
)

const Msg = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      px: '14px',
      py: '18px',
      fontFamily: FONT_SANS,
      fontSize: 12,
      color: TOKENS.ink400,
    }}
  >
    {children}
  </Box>
)
